/**
 * 权限检查工具
 * 基于菜单分配进行权限验证，而不是硬编码的角色类型
 */

/**
 * 获取用户菜单列表（从localStorage或传入）
 * @param {Array} menuList 菜单列表（可选，如果不传则从localStorage获取）
 * @returns {Array} 菜单列表
 */
export function getUserMenuList(menuList = null) {
  if (menuList && Array.isArray(menuList)) {
    return menuList
  }
  
  // 从localStorage获取菜单列表
  try {
    const menuListStr = localStorage.getItem('userMenuList')
    if (menuListStr) {
      return JSON.parse(menuListStr)
    }
  } catch (e) {
    console.warn('解析菜单列表失败:', e)
  }
  
  return []
}

/**
 * 统一处理菜单路径（与Home.vue中的getMenuIndex逻辑一致）
 * @param {Object} menu 菜单对象
 * @returns {String|null} 处理后的路径
 */
function normalizeMenuPath(menu) {
  if (!menu) return null
  
  // 优先使用 path
  if (menu.path) {
    let path = menu.path
    
    // 确保路径以 /home 开头
    if (!path.startsWith('/home')) {
      if (path.startsWith('/')) {
        path = `/home${path}`
      } else {
        path = `/home/${path}`
      }
    }

    // 针对 manager 路径的显式映射
    if (path === '/home/manager/account' || path === '/home/manager/user') {
      return '/home/manager-user'
    }
    if (path === '/home/manager/company') {
      return '/home/manager-company'
    }
    if (path === '/home/manager/report') {
      return '/home/manager-report'
    }
    if (path === '/home/manager/customer') {
      return '/home/manager-customer'
    }
    if (path === '/home/manager/approval') {
      return '/home/manager-approval'
    }
    
    // 针对 internal 路径的显式映射
    if (path === '/home/internal/approval') {
      return '/home/internal-approval'
    }
    
    // 针对 salesman 路径的显式映射
    if (path === '/home/salesman/business-trip' || path === '/home/salesman-business-trip' || path === '/home/salesman-trip') {
      return '/home/salesman-business-trip'
    }
    if (path === '/home/salesman/return-order' || path === '/home/salesman-return-order' || path === '/home/salesman-return') {
      return '/home/salesman-return-order'
    }
    if (path === '/home/salesman/deal-record' || path === '/home/salesman-deal-record' || path === '/home/salesman-deal') {
      return '/home/salesman-deal-record'
    }
    
    // 将路径中的斜杠转换为连字符
    if (path.includes('/manager/') || path.includes('/salesman/') || path.includes('/internal/') || path.includes('/accountant/')) {
      path = path.replace(/\/(manager|salesman|internal|accountant)\//g, '/$1-')
    }
    
    return path
  }
  
  // 其次使用 menuCode
  if (menu.menuCode) {
    return `/home/${menu.menuCode}`
  }
  
  return null
}

/**
 * 递归收集所有菜单路径
 * @param {Array} menus 菜单列表
 * @param {Array} paths 路径列表（用于递归）
 * @returns {Array} 所有菜单路径
 */
function collectMenuPaths(menus, paths = []) {
  if (!menus || !Array.isArray(menus)) {
    return paths
  }
  
  for (const menu of menus) {
    const path = normalizeMenuPath(menu)
    if (path) {
      paths.push(path)
    }
    
    // 递归处理子菜单
    if (menu.children && Array.isArray(menu.children) && menu.children.length > 0) {
      collectMenuPaths(menu.children, paths)
    }
  }
  
  return paths
}

/**
 * 规范化路由路径（与 normalizeMenuPath 逻辑一致）
 * @param {String} path 路径
 * @returns {String} 规范化后的路径
 */
function normalizeRoutePath(path) {
  if (!path) return path
  
  // 针对特定路径的映射
  if (path === '/home/salesman-trip' || path === '/home/salesman/business-trip') {
    return '/home/salesman-business-trip'
  }
  if (path === '/home/salesman-return' || path === '/home/salesman/return-order') {
    return '/home/salesman-return-order'
  }
  if (path === '/home/salesman-deal' || path === '/home/salesman/deal-record') {
    return '/home/salesman-deal-record'
  }
  
  // 针对 accountant 路径的映射：会计相关路径统一映射到 /home/accountant
  if (path.startsWith('/home/accountant')) {
    return '/home/accountant'
  }

  // 审批详情页面继承"审批管理"菜单权限
  // 例如：/home/approval-detail/123 -> /home/manager-approval 或 /home/internal-approval
  // 需要检查用户是否有任一审批管理菜单权限
  if (path.startsWith('/home/approval-detail')) {
    // 这里返回原路径，让权限检查逻辑处理
    // 实际权限检查会在 hasRoutePermission 中通过路径前缀匹配处理
    return path
  }
  
  return path
}

/**
 * 检查路由路径是否匹配菜单路径
 * @param {String} routePath 路由路径
 * @param {String} menuPath 菜单路径
 * @returns {Boolean} 是否匹配
 */
function isPathMatch(routePath, menuPath) {
  if (!routePath || !menuPath) return false
  
  // 规范化路径
  const normalizedRoutePath = normalizeRoutePath(routePath)
  // menuPath 已经是规范化后的路径（从 collectMenuPaths 中获取）
  const normalizedMenuPath = normalizeRoutePath(menuPath)
  
  // 精确匹配
  if (normalizedRoutePath === normalizedMenuPath) {
    return true
  }
  
  // 路径前缀匹配（处理子路由）
  // 例如：/home/salesman-return-order-form 匹配 /home/salesman-return-order
  if (normalizedRoutePath.startsWith(normalizedMenuPath + '/')) {
    return true
  }
  
  // 反向匹配（菜单路径是路由路径的前缀）
  if (normalizedMenuPath && normalizedMenuPath.startsWith(normalizedRoutePath + '/')) {
    return true
  }
  
  // 表单页面继承父页面权限：表单页面（如 /home/salesman-contract-form）匹配父页面（如 /home/salesman-contract）
  // 移除 -form 后缀后匹配父页面
  if (normalizedRoutePath.endsWith('-form')) {
    const parentPath = normalizedRoutePath.replace(/-form$/, '')
    if (parentPath === normalizedMenuPath) {
      return true
    }
  }
  
  return false
}

/**
 * 检查用户是否有访问指定路由的权限
 * @param {String} routePath 路由路径
 * @param {Array} menuList 菜单列表（可选）
 * @returns {Boolean} 是否有权限
 */
export function hasRoutePermission(routePath, menuList = null) {
  if (!routePath) return false
  
  // 首页始终允许访问
  if (routePath === '/home' || routePath === '/') {
    return true
  }
  
  // 获取用户菜单列表
  const userMenus = getUserMenuList(menuList)
  
  if (!userMenus || userMenus.length === 0) {
    // 如果没有菜单列表，系统管理员默认允许访问
    try {
      const userInfoStr = localStorage.getItem('userInfo')
      if (userInfoStr) {
        const userInfo = JSON.parse(userInfoStr)
        if (userInfo.userType === 'SYSTEM_ADMIN') {
          return true
        }
      }
    } catch (e) {
      console.warn('解析用户信息失败:', e)
    }
    return false
  }
  
  // 收集所有菜单路径
  const menuPaths = collectMenuPaths(userMenus)

  // 调试日志（开发环境）
  if (process.env.NODE_ENV === 'development') {
    console.log('[权限检查] 路由路径:', routePath)
    console.log('[权限检查] 菜单路径列表:', menuPaths)
  }

  // 检查路由路径是否匹配任何菜单路径
  for (const menuPath of menuPaths) {
    if (isPathMatch(routePath, menuPath)) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[权限检查] 匹配成功:', routePath, '->', menuPath)
      }
      return true
    }
  }
  
  // 特殊处理：审批详情页面需要检查用户是否有任一审批管理菜单权限
  if (routePath.startsWith('/home/approval-detail')) {
    const hasManagerApproval = menuPaths.some(path => 
      path === '/home/manager-approval' || path === '/home/manager/approval'
    )
    const hasInternalApproval = menuPaths.some(path => 
      path === '/home/internal-approval' || path === '/home/internal/approval'
    )
    if (hasManagerApproval || hasInternalApproval) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[权限检查] 审批详情页面权限检查通过')
      }
      return true
    }
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('[权限检查] 无匹配的菜单路径')
  }

  return false
}

/**
 * 检查用户是否有访问指定路由的权限（异步版本，会重新获取菜单）
 * @param {String} routePath 路由路径
 * @returns {Promise<Boolean>} 是否有权限
 */
export async function hasRoutePermissionAsync(routePath) {
  if (!routePath) return false
  
  // 首页始终允许访问
  if (routePath === '/home' || routePath === '/') {
    return true
  }
  
  try {
    // 从localStorage获取用户信息
    const userInfoStr = localStorage.getItem('userInfo')
    if (!userInfoStr) {
      return false
    }
    
    const userInfo = JSON.parse(userInfoStr)
    const userType = userInfo.userType || ''
    const isMember = userInfo.isMember || 0
    
    // 系统管理员默认允许访问
    if (userType === 'SYSTEM_ADMIN') {
      return true
    }
    
    // 动态获取用户菜单列表
    const { getUserMenuTree } = await import('../api/sysMenu')
    const res = await getUserMenuTree(userType, isMember, 'PC')
    
    if (res.code === '000000' && res.data) {
      // 保存到localStorage以便后续使用
      localStorage.setItem('userMenuList', JSON.stringify(res.data))
      return hasRoutePermission(routePath, res.data)
    }
    
    return false
  } catch (error) {
    console.error('检查路由权限失败:', error)
    return false
  }
}

